import * as bootstrap from 'bootstrap';
import $ from 'jquery';
import moment from 'moment';
import '@mux/mux-player';

import { v1BaseUrl } from './config.js';
import { detailsNavBarHtml, showLoader, hideLoader } from '/js/navigation.js';
import { renderLibrary } from '/js/library.js';
import { navigate } from '/js/router.js';
import { mkToast } from '@shared/js/toasts.js';
import { isPlayableUrl, preferredPlaybackMode, reportInvalidPlaybackSource, watchPlayerErrors } from '@shared/js/muxPlayer.js';
import { userData } from '/js/accounts.js';

// Build the absolute URL for a server-gated proxy path returned by the API.
function absUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  // API responses come back with paths like "/v1/content/file?id=..."; resolve
  // them against the API host so cookies follow.
  try {
    return new URL(path, v1BaseUrl).toString();
  } catch (_) {
    return path;
  }
}

// Escape HTML to defend the description and captions against XSS.
function escapeHtml(text) {
  return $('<div>').text(text == null ? '' : String(text)).html();
}

// Format seconds (or null) as MM:SS / H:MM:SS for the resume label.
function formatPosition(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${m}:${pad(s)}`;
}

function fetchShowExtras(showId) {
  return new Promise((resolve, reject) => {
    $.ajax({
      url: v1BaseUrl + 'show/extras',
      type: 'POST',
      svErrorMode: 'default',
      xhrFields: { withCredentials: true },
      data: { show: showId },
      dataType: 'json',
      success: (data) => resolve(data),
      error: (xhr) => reject(new Error(xhr?.responseJSON?.error || 'Unable to load show details')),
    });
  });
}

// Open a fullscreen native viewer for the given media. Closes on Escape or
// when the user taps outside the content area. `onClose` fires after every
// teardown path (X button, Escape, backdrop click, programmatic close) so
// callers can detach listeners they bound for the lifetime of the overlay.
function openLightbox({ titleHtml, bodyEl, onClose }) {
  const overlay = $('<div class="sv-lightbox" tabindex="-1"></div>');
  const header = $('<div class="sv-lightbox__header"></div>');
  header.append(
    $(`<div class="fs-6">${titleHtml || ''}</div>`),
    $('<button type="button" class="btn btn-sm btn-outline-light"><i class="bi bi-x-lg me-1"></i>Close</button>').on('click', close),
  );
  const body = $('<div class="sv-lightbox__body"></div>').append(bodyEl);
  overlay.append(header, body);
  $('body').append(overlay);

  let closed = false;
  function close() {
    if (closed) return;
    closed = true;
    overlay.remove();
    $(document).off('keydown.svLightbox');
    if (typeof onClose === 'function') onClose();
  }

  $(document).on('keydown.svLightbox', (e) => {
    if (e.key === 'Escape') close();
  });

  // Click outside the body content (header click also closes).
  overlay.on('mousedown', (e) => {
    if (e.target === overlay[0]) close();
  });

  return { close };
}

function openPhotoLightbox(url, captionHtml) {
  const img = $('<img alt="">').attr('src', url);
  openLightbox({ titleHtml: captionHtml || '', bodyEl: img });
}

async function openPdfLightbox(url, captionHtml) {
  // Render the PDF ourselves via PDF.js into <canvas> elements rather than
  // letting Chrome/Firefox/Safari hand off to their built-in PDF viewer.
  // The native viewers run in a separate renderer / process and handle
  // their own context menu (Save As, Print, etc.) before any event bubbles
  // to the page — so DOM-level right-click suppression on an <object> can
  // never block them. Canvases are normal DOM nodes, so contextmenu, drag,
  // text-selection, and keyboard shortcuts are all preventable from JS.
  // The raw PDF still arrives in the browser; this is a UI lock, not DRM.
  const viewer = $('<div class="sv-pdf-viewer"></div>');
  // Block right-click "Save Image As" on every page canvas, plus drag-out
  // and accidental highlighting that betrays the underlying data.
  viewer.on('contextmenu dragstart selectstart', (e) => e.preventDefault());

  const blockShortcuts = (e) => {
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;
    const k = (e.key || '').toLowerCase();
    // Ctrl/Cmd+S (save), Ctrl/Cmd+P (print), Ctrl/Cmd+A (select all — only
    // relevant if a future canvas-overlaid text layer is added, but cheap
    // to block now).
    if (k === 's' || k === 'p' || k === 'a') {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  document.addEventListener('keydown', blockShortcuts, true);

  const lb = openLightbox({
    titleHtml: captionHtml || 'Program',
    bodyEl: viewer,
    onClose: () => document.removeEventListener('keydown', blockShortcuts, true),
  });

  const loading = $('<div class="sv-pdf-status"><div class="spinner-border" role="status"></div></div>');
  viewer.append(loading);

  try {
    // Code-split PDF.js so the library only ships when a user actually opens
    // a program — the main entry stays small.
    const pdfjsLib = await import('pdfjs-dist');
    const workerMod = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerMod.default;

    // Fetch the PDF via the gated proxy with cookies attached. Reading the
    // bytes into an ArrayBuffer here means PDF.js never makes a second
    // request — the proxy is hit exactly once per open.
    const resp = await fetch(url, { credentials: 'include' });
    if (!resp.ok) throw new Error('Proxy returned ' + resp.status);
    const data = await resp.arrayBuffer();

    const pdf = await pdfjsLib.getDocument({ data }).promise;
    loading.remove();

    // Render each page to its own canvas, stacked vertically. The scale is
    // chosen relative to device pixel ratio so the output stays crisp on
    // HiDPI displays without ballooning memory on phones.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const baseScale = 1.5;
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: baseScale * dpr });
      const canvas = document.createElement('canvas');
      canvas.className = 'sv-pdf-page';
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      // Display size at the un-scaled viewport so HiDPI canvases don't
      // render at 2× pixel size in CSS.
      const displayViewport = page.getViewport({ scale: baseScale });
      canvas.style.width = Math.floor(displayViewport.width) + 'px';
      canvas.style.height = Math.floor(displayViewport.height) + 'px';
      viewer[0].appendChild(canvas);
      await page.render({
        canvasContext: canvas.getContext('2d'),
        viewport,
      }).promise;
    }
  } catch (err) {
    console.error('PDF render error:', err);
    loading.remove();
    viewer.append('<div class="sv-pdf-status text-light">Unable to display this program.</div>');
  }
}

function openVideoLightbox(extra, captionHtml) {
  const loading = $('<div class="text-light"><div class="spinner-border" role="status"></div></div>');
  // Detach the player's Sentry listener with the lightbox so reopening a clip
  // doesn't stack handlers on successive players.
  let detachPlayerErrors = null;
  const lb = openLightbox({
    titleHtml: captionHtml || extra.name || 'Extras',
    bodyEl: loading,
    onClose: () => {
      if (detachPlayerErrors) detachPlayerErrors();
      detachPlayerErrors = null;
    },
  });

  $.ajax({
    url: v1BaseUrl + 'show/extraStream',
    type: 'POST',
    svErrorMode: 'default',
    xhrFields: { withCredentials: true },
    data: { extra: extra.guid },
    dataType: 'json',
    success: function (data) {
      if (!data?.authorized || !isPlayableUrl(data?.url)) {
        // A source the player can't load is worth a report: the media element
        // would otherwise fetch it off our own host and fail with an opaque
        // "Source Not Supported" dialog.
        if (data?.authorized) {
          reportInvalidPlaybackSource(data?.url, { endpoint: 'show/extraStream', extraGuid: extra.guid });
        }
        mkToast('details', 'danger', 'Error', 'Unable to play this clip.', 5000);
        lb.close();
        return;
      }
      // Attributes go on via .attr() so a URL or title can't break the markup.
      const player = $('<mux-player accent-color="#922727" stream-type="on-demand" autoplay="muted"></mux-player>')
        .attr('src', data.url)
        .attr('metadata-video-title', extra.name || 'Extras')
        .attr('metadata-viewer-user-id', userData.guid);
      const preferPlayback = preferredPlaybackMode();
      if (preferPlayback) player.attr('prefer-playback', preferPlayback);
      detachPlayerErrors = watchPlayerErrors(player[0], {
        source: 'show-extra',
        extraGuid: extra.guid,
        showId: extra.show_guid,
      });
      loading.replaceWith(player);
    },
    error: function () {
      mkToast('details', 'danger', 'Error', 'Unable to play this clip.', 5000);
      lb.close();
    },
  });
}

// Public entry point. Replaces the current app contents with the show details
// interstitial. The library card click routes here instead of jumping
// straight into the player.
export async function renderShowDetails(showId) {
  const app = $('#app');
  app.empty();
  // Details nav uses the leftmost back arrow (returns to /) instead of the
  // standard brand title — matches the pattern the user requested.
  app.append(detailsNavBarHtml());
  showLoader();

  let payload;
  try {
    payload = await fetchShowExtras(showId);
  } catch (err) {
    hideLoader();
    // Bounce back to the vault and replace history so a stale /shows/:id URL
    // doesn't linger if the show isn't actually available to this user.
    navigate('/', { replace: true });
    mkToast('details', 'danger', 'Error', err.message || 'Unable to load show details', 8000);
    return;
  }
  hideLoader();

  const { show, extras, progress } = payload;
  if (!show) {
    navigate('/', { replace: true });
    mkToast('details', 'danger', 'Error', 'Show not found', 5000);
    return;
  }

  const released = moment(show.releaseAt).unix() <= moment().unix();
  const banner = extras.banner ? absUrl(extras.banner.url) : null;
  const poster = show.coverArt || '';

  const container = $('<div class="details-container"></div>');

  // Hero (2:1 banner). The banner backs the whole hero *and* the title/CTA
  // block below it — the gradient only turns fully opaque right before the
  // description starts. If no banner uploaded, fall back to a colored block.
  const hero = $('<div class="details-hero"></div>');
  if (banner) {
    hero.css('background-image', `url('${banner}')`);
  } else {
    hero.addClass('details-hero--placeholder');
  }
  hero.append($('<div class="details-hero__spacer"></div>'));
  container.append(hero);

  // Title section overlapping the hero.
  const meta = $('<div class="container details-meta pb-4"></div>');
  const row = $('<div class="row g-4 align-items-end"></div>');

  const posterCol = $('<div class="col-6 col-md-3"></div>');
  const posterEl = $('<div class="details-poster"></div>');
  if (poster) posterEl.css('background-image', `url('${poster}')`);
  posterCol.append(posterEl);

  const titleCol = $('<div class="col-12 col-md-9"></div>');
  const titleStack = $('<div class="vstack gap-1"></div>');
  titleStack.append($(`<h1 class="display-5 fw-bold mb-0">${escapeHtml(show.title)}</h1>`));
  if (show.subtitle) {
    titleStack.append($(`<div class="fs-4 text-light-emphasis">${escapeHtml(show.subtitle)}</div>`));
  }
  const monthYear = show.performanceDate ? moment(show.performanceDate).format('MMMM YYYY') : null;
  const metaBits = [show.category, monthYear].filter(Boolean).map(escapeHtml).join(' &middot; ');
  if (metaBits) {
    titleStack.append($(`<div class="text-light">${metaBits}</div>`));
  }

  // Attribute the presenting organization.
  if (show.organization && show.organization.name) {
    const attribution = $('<div class="d-flex align-items-center text-light-emphasis mt-1"></div>');
    if (show.organization.icon) {
      attribution.append(
        $('<img class="rounded-2 me-2" style="width: 20px; height: 20px; object-fit: cover;" alt="">')
          .attr('src', show.organization.icon)
      );
    } else {
      attribution.append($('<i class="bi bi-building me-2"></i>'));
    }
    attribution.append(
      $('<span></span>').text('Presented by '),
      $('<span class="text-light ms-1"></span>').text(show.organization.name)
    );
    titleStack.append(attribution);
  }

  // Build the Play / Resume CTA. When saved progress exists the primary
  // button reads "Resume" with a thin line along its bottom edge visualizing
  // how far through the show the user is, plus a secondary "Play from
  // Beginning" button. State — "Available <date>", "Currently Unavailable",
  // etc. — is shown as a small hint above the button row rather than
  // replacing the label, so users always know what the action would be once
  // it's available.
  const hasProgress = progress > 0;
  const playLabel = hasProgress
    ? '<i class="bi bi-play-fill me-1"></i> Resume'
    : '<i class="bi bi-play-fill me-1"></i> Play';
  const playBtn = $(`<button type="button" class="btn btn-primary btn-lg px-4">${playLabel}</button>`);
  const duration = Number(show.duration) || 0;
  if (hasProgress && duration > 0) {
    // Floor at 3% so a just-started show still renders a visible sliver.
    const pct = Math.min(100, Math.max(3, (progress / duration) * 100));
    playBtn.addClass('btn-play-progress').attr('title', `Resume from ${formatPosition(progress)}`);
    playBtn.append(
      $('<span class="btn-play-progress__track" aria-hidden="true"></span>')
        .append($('<span class="btn-play-progress__bar"></span>').css('width', pct + '%'))
    );
  }

  let unavailableReason = null;
  if (!userData.emailVerified) {
    unavailableReason = 'Verify your email to watch';
  } else if (!released) {
    unavailableReason = `Available ${moment(show.releaseAt).format('MMMM Do')}`;
  } else if (!show.ready) {
    unavailableReason = 'Currently unavailable';
  }
  if (unavailableReason) {
    playBtn.prop('disabled', true);
  }

  playBtn.on('click', () => {
    // The button is disabled when not playable, so this only fires when the
    // show is genuinely available.
    navigate('/shows/' + encodeURIComponent(show.id) + '/watch');
  });

  const ctaGroup = $('<div class="mt-3"></div>');
  if (unavailableReason) {
    ctaGroup.append(
      $(`<div class="text-warning small mb-2"><i class="bi bi-info-circle me-1"></i>${escapeHtml(unavailableReason)}</div>`)
    );
  }
  const ctaRow = $('<div class="hstack gap-2 flex-wrap"></div>');
  ctaRow.append(playBtn);

  if (hasProgress) {
    const restartBtn = $('<button type="button" class="btn btn-outline-light btn-lg"><i class="bi bi-arrow-counterclockwise me-1"></i> Start Over</button>');
    if (unavailableReason) restartBtn.prop('disabled', true);
    restartBtn.on('click', () => {
      // ?start=0 tells the player to skip the auto-resume seek.
      navigate('/shows/' + encodeURIComponent(show.id) + '/watch?start=0');
    });
    ctaRow.append(restartBtn);
  }

  if (extras.program) {
    const programBtn = $('<button type="button" class="btn btn-outline-light btn-lg"><i class="bi bi-journal-text me-1"></i> View Program</button>');
    programBtn.on('click', () => openPdfLightbox(absUrl(extras.program.url), 'Program'));
    ctaRow.append(programBtn);
  }

  ctaGroup.append(ctaRow);
  titleStack.append(ctaGroup);
  titleCol.append(titleStack);
  row.append(posterCol, titleCol);
  meta.append(row);
  hero.append(meta);

  // Description and extras.
  const body = $('<div class="container pb-5"></div>');

  if (extras.description) {
    const descSection = $('<section class="mt-4"></section>');
    descSection.append($(`<p class="details-description fs-5">${escapeHtml(extras.description)}</p>`));
    body.append(descSection);
  }

  if (extras.photos && extras.photos.length) {
    const sec = $('<section></section>');
    sec.append('<h2 class="extras-section-title h4">Photos</h2>');
    const grid = $('<div class="row row-cols-2 row-cols-sm-3 row-cols-md-4 g-3"></div>');
    extras.photos.forEach((p) => {
      const url = absUrl(p.url);
      const tile = $('<div class="extras-tile"></div>').css('background-image', `url('${url}')`);
      if (p.caption) tile.append($(`<div class="extras-tile__caption">${escapeHtml(p.caption)}</div>`));
      tile.on('click', () => openPhotoLightbox(url, p.caption ? escapeHtml(p.caption) : ''));
      grid.append($('<div class="col"></div>').append(tile));
    });
    sec.append(grid);
    body.append(sec);
  }

  if (extras.videos && extras.videos.length) {
    const sec = $('<section></section>');
    sec.append('<h2 class="extras-section-title h4">Extras</h2>');
    const grid = $('<div class="row row-cols-1 row-cols-sm-2 row-cols-md-3 g-3"></div>');
    extras.videos.forEach((v) => {
      const tile = $('<div class="extras-tile extras-tile--video"></div>');
      const labelParts = [];
      if (v.caption) labelParts.push(escapeHtml(v.caption));
      else if (v.name) labelParts.push(escapeHtml(v.name));
      if (v.duration) labelParts.push(formatPosition(v.duration));
      if (labelParts.length) {
        tile.append($(`<div class="extras-tile__caption">${labelParts.join(' &middot; ')}</div>`));
      }
      if (!v.ready) {
        tile.css('opacity', 0.6).attr('title', 'Still processing');
      } else {
        tile.on('click', () => openVideoLightbox(v, v.caption ? escapeHtml(v.caption) : ''));
      }
      grid.append($('<div class="col"></div>').append(tile));
    });
    sec.append(grid);
    body.append(sec);
  }

  container.append(body);
  app.append(container);
}
